"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useSetAtom } from "jotai";
import { AppsSideNav } from "./AppsSideNav";
import { DashboardSideNav } from "./DashboardSideNav";
import { InboxSideNav } from "./InboxSideNav";
import { ManageWorkspaceSideNav } from "./ManageWorkspaceSideNav";
import { SettingsSideNav } from "./SettingsSideNav";
import { toolSidenavTargetAtom } from "./atoms";
import { ResolvedSidenav, resolveSidenav } from "./resolveSidenav";
import { useAllowedSections } from "@/components/Layouts/pageSections";
import { usePermissions } from "@/hooks/usePermissions";

// Which sidenav is showing: a new one is mounted fresh when this changes.
const sidenavKey = (sidenav: ResolvedSidenav) => {
  switch (sidenav.kind) {
    case "inbox":
      return `inbox:${sidenav.inboxId}`;
    case "workspace":
      return `workspace:${sidenav.workspaceId ?? "current"}`;
    default:
      return sidenav.kind;
  }
};

// The sidenav a dashboard route shows. Its own component, so only it follows
// query changes (an inbox's `?inboxId=`, a workspace section's `?tab=`).
export const DashboardSidenavSlot = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const setToolTarget = useSetAtom(toolSidenavTargetAtom);
  const workspaceSections = useAllowedSections("workspace");
  // Unknown until the permissions land; deciding before would switch sidenavs.
  const { isLoading: permissionsLoading } = usePermissions();
  const sidenav = resolveSidenav(
    pathname ?? "",
    searchParams,
    permissionsLoading
      ? undefined
      : workspaceSections.map((section) => section.id)
  );

  const content = (() => {
    switch (sidenav.kind) {
      case "tool":
        // ToolLayout portals the tool's own sidenav in here.
        return <div ref={setToolTarget} className="flex h-full" />;
      case "inbox":
        return <InboxSideNav inboxId={sidenav.inboxId} />;
      case "workspace":
        return <ManageWorkspaceSideNav workspaceId={sidenav.workspaceId} />;
      case "settings":
        return <SettingsSideNav />;
      case "apps":
        return <AppsSideNav />;
      case "main":
        return <DashboardSideNav />;
    }
  })();

  return (
    <div key={sidenavKey(sidenav)} className="flex h-full shrink-0">
      {content}
    </div>
  );
};
